/**
 * Turning an admin's brand color into CSS. The app's accent ramp lives in
 * `--brand-*` custom properties (defaults on `:root` in index.css); this builds
 * the override that replaces them.
 *
 * The derivation itself is in `@alpha/shared` so the math is tested once, in
 * the package where this repo keeps its pure logic.
 */
import { deriveBrandRamp, readableAccentText, readableForeground } from "@alpha/shared";

/**
 * Last color we saw, so a returning visitor doesn't watch the app paint its own
 * blue and then repaint. Settings arrive over the network, after first paint.
 */
export const BRAND_COLOR_CACHE_KEY = "alpha.brandColor";

/**
 * The `:root` override for a brand color, or null when there is nothing to
 * override — no color set, or a stored value we can't parse.
 */
export function brandThemeCss(seed: string | null | undefined): string | null {
  if (!seed) return null;
  try {
    const declarations = Object.entries(deriveBrandRamp(seed)).map(
      ([stop, channels]) => `--brand-${stop}:${channels}`,
    );
    declarations.push(`--brand-fg:${readableForeground(seed)}`);
    declarations.push(`--brand-text:${readableAccentText(seed)}`);
    return `:root{${declarations.join(";")}}`;
  } catch {
    // A hand-edited cache entry, or a value written by a newer schema. Falling
    // back to the shipped blue beats rendering a broken stylesheet.
    return null;
  }
}

/** Null rather than throwing where storage is unavailable — Safari private mode, SSR. */
function storage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function readCachedBrandColor(): string | null {
  try {
    return storage()?.getItem(BRAND_COLOR_CACHE_KEY) ?? null;
  } catch {
    // Safari private mode throws on access rather than returning null.
    return null;
  }
}

export function writeCachedBrandColor(color: string | null): void {
  const store = storage();
  if (!store) return;
  try {
    if (color) store.setItem(BRAND_COLOR_CACHE_KEY, color);
    else store.removeItem(BRAND_COLOR_CACHE_KEY);
  } catch {
    // A full or locked-down store is not worth failing a render over.
  }
}
